/**
 * Shared ScheduleEngine — the single implementation of
 * ScheduleEngineContract used by both the API materialisation and the web
 * editor preview (docs/plans/planned-task-schedule.md §2).
 *
 * Thin by design: every rule lives in a pure module beside this one
 * (compile / expand / exceptions / policy / after-service / describe). The
 * class only sequences them:
 *
 *   pattern expansion → exceptions (task ∪ line ∪ plan) → non-working-day policy
 */

import { ScheduleRule } from './schedule.rule';
import { ScheduleEngineContract } from './schedule.engine.contract';
import { CompiledTaskSchedule, evaluableScheduleContext, ScheduleChange, ScheduleContext, ScheduleExceptions } from './schedule.types';
import { addDays, compileAnchor, compileSchedule, isoWeekday, isWorkingDaysCalendar, normaliseScheduleDates } from './schedule.compile';
import { expandRule, nextRuleOccurrence } from './schedule.expand';
import { applyExceptionChange, applyExceptions } from './schedule.exceptions';
import { applyNonWorkingDayPolicy } from './schedule.policy';
import { addMonthsClamped, afterServiceNextDue } from './schedule.after.service';
import { describeSchedule, describeScheduleShort } from './schedule.describe';
import { effectiveScheduleRule } from './schedule.effective';

/** Search horizon for nextAfter when the schedule has no natural end. Ten years covers every §3 pattern. */
const NEXT_AFTER_HORIZON_YEARS = 10;

/** Window used to probe one step at a time in nextAfter, so a sparse rule costs a handful of expansions. */
const NEXT_AFTER_PROBE_DAYS = 62;

/**
 * How far nextAfter looks for the next working day. Two weeks: any non-empty work week hits within
 * seven days, and the extra week absorbs a run of holidays without pretending an empty work week
 * has an answer somewhere further out.
 */
const WORKING_DAY_PROBE_DAYS = 14;

/**
 * How far past the last listed date nextAfter looks for a 'dates' schedule. Matches the policy's own
 * chase window (a fortnight), which is the furthest a shift can carry the final date.
 */
const LISTED_SHIFT_PROBE_DAYS = 14;

export class ScheduleEngine implements ScheduleEngineContract {

    compile(stored: ScheduleRule | undefined): CompiledTaskSchedule {

        return compileSchedule(effectiveScheduleRule(stored));
    }

    occurrencesBetween(stored: ScheduleRule | undefined, from: string, to: string, context: ScheduleContext, exceptions?: ScheduleExceptions): string[] {

        const editor = effectiveScheduleRule(stored);

        // WBS-11017: an EMPTY work week arriving here would make every working-day task due on zero
        // days, and every non-working-day shift chase forever. Healed at the boundary rather than in
        // each private path, so no future rule can reintroduce the hole by forgetting it.
        const ctx = evaluableScheduleContext(context);
        const effectiveTo = ctx.effectiveTo && to > ctx.effectiveTo ? ctx.effectiveTo : to;

        // After-service dues derive from lastCompleted + interval and may legitimately precede the
        // line's effectiveFrom (overdue work) — only the caller's window and effectiveTo clip them.
        if (editor?.kind === 'afterService') {
            if (from > effectiveTo) {
                return [];
            }

            return this.afterServiceOccurrences(editor, from, effectiveTo, ctx, exceptions);
        }

        const effectiveFrom = from < ctx.effectiveFrom ? ctx.effectiveFrom : from;
        if (effectiveFrom > effectiveTo) {
            return [];
        }

        if (this.isWorkingDays(editor)) {
            return this.workingDayOccurrences(effectiveFrom, effectiveTo, ctx, exceptions);
        }

        if (editor!.kind === 'perVisit') {
            return [];
        }

        if (editor!.kind === 'dates') {
            return this.listedDateOccurrences(editor!, effectiveFrom, effectiveTo, ctx, exceptions);
        }

        return this.calendarOccurrences(editor!, effectiveFrom, effectiveTo, ctx, exceptions);
    }

    nextAfter(stored: ScheduleRule | undefined, date: string, context: ScheduleContext, exceptions?: ScheduleExceptions): string | undefined {

        const editor = effectiveScheduleRule(stored);

        const ctx = evaluableScheduleContext(context);
        const start = addDays(date, 1);
        if (ctx.effectiveTo && start > ctx.effectiveTo) return undefined;

        if (this.isWorkingDays(editor)) {
            // Bounded by a fortnight rather than the ten-year horizon: a working week with at least
            // one day in it always yields inside seven days, and a tenant that has configured none
            // has no next working day to find at any distance.
            const probeEnd = this.minDate(addDays(start, WORKING_DAY_PROBE_DAYS), ctx.effectiveTo ?? addDays(start, WORKING_DAY_PROBE_DAYS));
            const [next] = this.workingDayOccurrences(start, probeEnd, ctx, exceptions);

            return next;
        }

        if (editor!.kind === 'perVisit') {
            return undefined;
        }

        if (editor!.kind === 'afterService') {
            const [next] = this.afterServiceOccurrences(editor!, start, this.minDate(this.horizonFrom(start), ctx.effectiveTo ?? this.horizonFrom(start)), ctx, exceptions);

            return next;
        }

        if (editor!.kind === 'dates') {
            // A listed schedule is finite, so there is no probe loop to run: the last date bounds the
            // search directly and the whole remaining list costs one pass.
            const listed = normaliseScheduleDates(editor!.dates);
            const last = listed[listed.length - 1];

            if (!last) {
                return undefined;
            }

            const searchStart = start < ctx.effectiveFrom ? ctx.effectiveFrom : start;
            // The search end is NOT simply the last listed date. Two things can put an occurrence
            // beyond it: an exception extra, and a forward shift under 'next'/'nearest', which can
            // carry the final listed date past itself. Both are covered by taking the later of the
            // list and the extras and then padding by the policy's chase window.
            const latest = this.maxDate(last, this.latestExtra(exceptions) ?? last);
            const policy = editor!.dates?.nonWorkingDayPolicy ?? 'keep';
            const horizon = policy === 'keep' || policy === 'skip' ? latest : addDays(latest, LISTED_SHIFT_PROBE_DAYS);
            const [next] = this.listedDateOccurrences(editor!, searchStart, this.minDate(horizon, ctx.effectiveTo ?? horizon), ctx, exceptions);

            return next;
        }

        return this.nextCalendarOccurrence(editor!, start, ctx, exceptions);
    }

    isDueInRange(editor: ScheduleRule | undefined, from: string, to: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): boolean {

        return this.occurrencesBetween(editor, from, to, ctx, exceptions).length > 0;
    }

    describe(editor: ScheduleRule | undefined): string {

        return describeSchedule(effectiveScheduleRule(editor));
    }

    describeShort(editor: ScheduleRule | undefined): string {

        return describeScheduleShort(effectiveScheduleRule(editor));
    }

    applyException(exceptions: ScheduleExceptions | undefined, change: ScheduleChange): ScheduleExceptions {

        return applyExceptionChange(exceptions, change);
    }

    /**
     * The working-day default (plan §2 "Absent schedule = due every working day").
     *
     * Two models reach it and they are NOT the same thing anywhere else in the system: an ABSENT
     * schedule (the default a task gets when nobody picked anything) and the explicit 'workingDays'
     * daily preset (someone chose it). They evaluate identically because they mean the same thing;
     * they stay distinct on the document because only one of them is a user's decision.
     *
     * `{ kind: 'perVisit', nth: 1 }` is deliberately NOT folded in here. It is the explicit
     * every-visit tack-on, has no calendar of its own, and yields zero occurrences.
     */
    private isWorkingDays(editor: ScheduleRule | undefined): boolean {

        if (!editor) {
            return true;
        }

        return editor.kind === 'calendar' && isWorkingDaysCalendar(editor.calendar);
    }

    /**
     * Every day in the window whose ISO weekday is in `ctx.workWeek`, minus exceptions.
     *
     * NO NON-WORKING-DAY POLICY RUNS HERE, and there is nothing to run: the pattern is defined AS
     * the working week, so an occurrence cannot land on a non-working day by construction. 'skip'
     * would have nothing to drop and 'next'/'previous'/'nearest' nothing to move — the shift is
     * already the identity. Holidays are the one part of "non-working" the day set does not cover,
     * so they are subtracted directly: a working-day task does not happen on a public holiday.
     */
    private workingDayOccurrences(from: string, to: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): string[] {

        const workWeek = new Set(ctx.workWeek ?? []);
        const holidays = new Set(ctx.holidays ?? []);
        const dates: string[] = [];

        for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) {
            if (!workWeek.has(isoWeekday(cursor)) || holidays.has(cursor)) {
                continue;
            }

            dates.push(cursor);
        }

        const excepted = applyExceptions(dates, exceptions, from, to, ctx.effectiveFrom);

        // Extra dates are the one thing that can reintroduce a non-working day, and they are meant
        // to: an RDATE is someone saying "also this Saturday". They are kept, then clipped to the
        // window and the line's effective-from like every other path.
        return excepted.filter(date => date >= from && date <= to && date >= ctx.effectiveFrom && (!ctx.effectiveTo || date <= ctx.effectiveTo));
    }

    /**
     * Calendar path: expand the pattern, subtract/add exceptions, then shift
     * for the non-working-day policy. The policy runs last so a shifted date
     * can never be resurrected by an exception.
     */
    private calendarOccurrences(editor: ScheduleRule, from: string, to: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): string[] {

        const calendar = editor.calendar;

        if (!calendar) {
            return [];
        }

        const compiled = compileSchedule(editor);

        if (!compiled.scheduleRule) {
            return [];
        }

        // Shifts can pull an occurrence from just outside the window into it,
        // so expand a padded window and clip after the policy has run.
        const padded = calendar.nonWorkingDayPolicy === 'keep' || calendar.nonWorkingDayPolicy === 'skip' ? 0 : 14;
        const expandFrom = padded > 0 ? addDays(from, -padded) : from;
        const expandTo = padded > 0 ? addDays(to, padded) : to;

        const anchor = compileAnchor(calendar, ctx.effectiveFrom);
        const pattern = expandRule(compiled.scheduleRule, anchor, expandFrom, expandTo);
        const excepted = applyExceptions(pattern, exceptions, expandFrom, expandTo, ctx.effectiveFrom);
        const endsOnDate = calendar.ends?.type === 'onDate' ? calendar.ends.date : undefined;
        const shifted = applyNonWorkingDayPolicy(excepted, calendar.nonWorkingDayPolicy, { workWeek: ctx.workWeek, holidays: ctx.holidays }, endsOnDate);

        return shifted.filter(date => date >= from && date <= to && date >= ctx.effectiveFrom && (!ctx.effectiveTo || date <= ctx.effectiveTo));
    }

    /**
     * Explicit date list path. Structurally the calendar path with the expansion step replaced by the
     * stored list, and it runs the same three stages in the same order so a listed date behaves like
     * any other occurrence: list → exceptions (a skip masks a listed date, an extra adds one) →
     * non-working-day policy.
     *
     * The padded window matters here for the same reason it does on the calendar path: a date just
     * outside the window can shift into it under 'next'/'previous'/'nearest'.
     */
    private listedDateOccurrences(editor: ScheduleRule, from: string, to: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): string[] {

        const listed = normaliseScheduleDates(editor.dates);

        if (listed.length === 0) {
            return [];
        }

        const policy = editor.dates?.nonWorkingDayPolicy ?? 'keep';
        const padded = policy === 'keep' || policy === 'skip' ? 0 : 14;
        const windowFrom = padded > 0 ? addDays(from, -padded) : from;
        const windowTo = padded > 0 ? addDays(to, padded) : to;
        const inWindow = listed.filter(date => date >= windowFrom && date <= windowTo);
        const excepted = applyExceptions(inWindow, exceptions, windowFrom, windowTo, ctx.effectiveFrom);
        // No ends clause is passed: the list IS the end, so there is no onDate boundary a shifted date
        // could be pushed past.
        const shifted = applyNonWorkingDayPolicy(excepted, policy, { workWeek: ctx.workWeek, holidays: ctx.holidays });

        return shifted.filter(date => date >= from && date <= to && date >= ctx.effectiveFrom && (!ctx.effectiveTo || date <= ctx.effectiveTo));
    }

    /** Latest exception extra, so nextAfter's finite search still reaches an extra past the last listed date. */
    private latestExtra(exceptions: ScheduleExceptions | undefined): string | undefined {

        const extras = (exceptions?.extras ?? []).map(extra => extra.date).sort();

        return extras[extras.length - 1];
    }

    /**
     * After-service path: a single next-due date, which still passes through
     * the calendar-free equivalent of the policy step so a shifted due date
     * behaves like any other occurrence.
     */
    private afterServiceOccurrences(editor: ScheduleRule, from: string, to: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): string[] {

        const afterService = editor.afterService;

        if (!afterService) {
            return [];
        }

        const due = afterServiceNextDue(afterService, ctx.effectiveFrom, ctx.today, ctx.lastCompleted, ctx.lastScheduledDue);
        const resolved = applyNonWorkingDayPolicy([due], 'keep', { workWeek: ctx.workWeek, holidays: ctx.holidays });

        // Extras are the one-off dates a person books on top of the chain (compliance "Book now"): the
        // chain has no calendar to skip, so only extras apply, and they never re-seed it.
        const extras = (exceptions?.extras ?? []).map(extra => extra.date);

        // effectiveFrom only defaults firstDue for after-service schedules; an overdue due date
        // computed from lastCompleted + interval may legitimately precede it and must still emit.
        return [...new Set([...resolved, ...extras])]
            .sort()
            .filter(date => date >= from && date <= to && (!ctx.effectiveTo || date <= ctx.effectiveTo));
    }

    /**
     * Walks the pattern forward in probe windows rather than expanding years,
     * so the cost tracks the gap to the next occurrence, not the horizon.
     */
    private nextCalendarOccurrence(editor: ScheduleRule, start: string, ctx: ScheduleContext, exceptions?: ScheduleExceptions): string | undefined {

        const compiled = compileSchedule(editor);
        const calendar = editor.calendar;

        if (!compiled.scheduleRule || !calendar) {
            return undefined;
        }

        const anchor = compileAnchor(calendar, ctx.effectiveFrom);
        const horizon = this.minDate(this.horizonFrom(start), ctx.effectiveTo ?? this.horizonFrom(start));
        let cursor = start;

        while (cursor <= horizon) {
            // Grow the probe window's END only — the FROM stays at `start` so a
            // pattern date beyond the probe that shifts backwards (policy
            // previous/nearest) can never be clipped out of the window the way
            // an advancing FROM would clip it. This keeps nextAfter equal to
            // occurrencesBetween(start, horizon)[0] by construction.
            const probeEnd = this.minDate(addDays(cursor, NEXT_AFTER_PROBE_DAYS), horizon);
            const found = this.calendarOccurrences(editor, start, probeEnd, ctx, exceptions);

            if (found.length > 0) {
                return found[0];
            }

            const nextRaw = nextRuleOccurrence(compiled.scheduleRule, anchor, probeEnd);

            if (!nextRaw) {
                return undefined;
            }

            cursor = nextRaw > probeEnd ? nextRaw : addDays(probeEnd, 1);
        }

        return undefined;
    }

    private horizonFrom(date: string): string {

        return addMonthsClamped(date, NEXT_AFTER_HORIZON_YEARS * 12);
    }

    private minDate(a: string, b: string): string {

        return a < b ? a : b;
    }

    private maxDate(a: string, b: string): string {

        return a > b ? a : b;
    }
}
